<script>
import Layout from "../../layouts/auth";
import appConfig from "@/app.config";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  page: {
    title: "Login",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
  },
  data() {
    return {
      submitted: false,
      data: {
        username: "",
        password: "",
      },
      showPassword: false, // Manage password visibility
      usernameError: "",
      passwordError: "",
    };
  },
  computed: {
    ...mapState("auth", ["status"]),
    ...mapGetters("auth", ["loginStatus", "login_errors"]),
  },
  methods: {
    ...mapActions({
      LogInFunction: "auth/login",
    }),
    tryToLogIn() {
      if (this.data.username == "") {
        this.usernameError = "Please enter a Username";
        return;
      } else {
        this.usernameError = "";
      }
      if (this.data.password == "") {
        this.passwordError = "Please enter a Password";
        return;
      } else {
        this.passwordError = "";
      }
      this.LogInFunction(this.data);
    },
    resetErrors() {
      this.passwordError = "";
      this.usernameError = "";
    },
    // Toggle password visibility
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
  },
  mounted() { },
};

</script>

<template>
  <Layout>
    <div class="login-page">

      <div class="login-box">

        <!-- LEFT BRAND PANEL -->
        <div class="login-brand">
          <div class="brand-content">

            <div class="brand-top">
              <h2>Production System</h2>
              <div class="brand-line"></div>
              <h4 class="py-3 text-white">Secure access to your business operations</h4>
            </div>
            <hr>
            <div class="brand-bottom">
              <p class="brand-note" style="font-size: 16px;">
                Manage production, track performance, and make better decisions — all in one system.
              </p>
            </div>

          </div>
        </div>

        <!-- RIGHT FORM PANEL -->
        <div class="login-form">

          <div class="form-header">
            <h4>Sign In</h4>
            <p>Enter your credentials to continue</p>
          </div>

          <b-form @submit.prevent="tryToLogIn">

            <!-- USERNAME -->
            <div class="form-group mb-3">
              <label>Username</label>
              <b-form-input v-model="data.username" type="text" placeholder="Enter username"
                @input="resetErrors()"></b-form-input>
              <small class="text-danger" v-if="usernameError">{{ usernameError }}</small>
            </div>

            <!-- PASSWORD -->
            <div class="form-group mb-3 position-relative">
              <label>Password</label>
              <b-form-input v-model="data.password" :type="showPassword ? 'text' : 'password'"
                placeholder="Enter password" @input="resetErrors()"></b-form-input>

              <span class="toggle-pass" @click="togglePasswordVisibility">
                {{ showPassword ? 'Hide' : 'Show' }}
              </span>

              <small class="text-danger" v-if="passwordError">{{ passwordError }}</small>
            </div>

            <!-- OPTIONS -->
            <div class="form-options">
              <b-form-checkbox> Remember me </b-form-checkbox>

              <router-link to="/forgot-password">
                Forgot password?
              </router-link>
            </div>

            <!-- BUTTON -->
            <b-button type="submit" class="btn-login w-100">
              Log In
              <b-spinner v-if="loginStatus" small></b-spinner>
            </b-button>

            <!-- ERROR -->
            <b-alert v-if="login_errors" show variant="danger" class="mt-3 text-center">
              {{ login_errors }}
            </b-alert>

          </b-form>

        </div>

      </div>

      <!-- FOOTER -->
      <div class="login-footer">
        © {{ new Date().getFullYear() }} Production System
      </div>

    </div>
  </Layout>
</template>



<style scoped>

/* =========================
   PAGE LAYOUT
========================= */
.login-page {
  min-height: 100vh;
  background: #f1f3f7;

  display: flex;
  flex-direction: column;
  justify-content: center; /* center ONLY on desktop */
  align-items: center;

  padding: 20px;
  box-sizing: border-box;
}

/* =========================
   MAIN CONTAINER
========================= */
.login-box {
  width: 900px;
  max-width: 100%;

  display: flex;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;

  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
}

/* =========================
   LEFT SIDE (BRAND PANEL)
========================= */
.login-brand {
  width: 40%;
  background: linear-gradient(135deg, #e612e6, #b10fb1);
  color: #ffffff;

  padding: 40px 30px;

  display: flex;
  align-items: flex-start; /* ✅ PUSH CONTENT UP */
}

/* BRAND CONTENT */
.brand-content {
  width: 100%;
  margin-top: 10px;
}

/* TITLE */
.brand-top h2 {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}

/* LINE */
.brand-line {
  width: 50px;
  height: 3px;
  background: #ffffff;
  margin: 10px 0 15px 0;
  border-radius: 2px;
}

/* DESCRIPTION */
.brand-top p {
  font-size: 14px;
  opacity: 0.95;
  line-height: 1.6;
}

/* EXTRA TEXT */
.brand-bottom {
  margin-top: 30px;
}

.brand-note {
  font-size: 13px;
  opacity: 0.85;
  line-height: 1.6;
}

/* =========================
   RIGHT SIDE (FORM)
========================= */
.login-form {
  width: 60%;
  padding: 35px;
}

/* HEADER */
.form-header {
  margin-bottom: 20px;
}

.form-header h4 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.form-header p {
  font-size: 13px;
  color: #777;
}

/* FORM GROUP */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 5px;
  display: block;
  color: #333;
}

/* INPUTS */
.form-control {
  height: 42px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 14px;
  padding: 0 12px;
  transition: all 0.2s ease;
}

.form-control:focus {
  border-color: #e612e6;
  box-shadow: 0 0 0 0.1rem rgba(230, 18, 230, 0.15);
}

/* PASSWORD TOGGLE */
.toggle-pass {
  position: absolute;
  right: 10px;
  top: 34px;
  font-size: 13px;
  color: #e612e6;
  cursor: pointer;
  user-select: none;
}

/* OPTIONS */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.form-options a {
  font-size: 13px;
  color: #e612e6;
  text-decoration: none;
}

.form-options a:hover {
  text-decoration: underline;
}

/* BUTTON */
.btn-login {
  background: #e612e6;
  border: none;
  height: 42px;
  font-weight: 600;
  font-size: 14px;
  border-radius: 6px;
  transition: background 0.2s ease;
}

.btn-login:hover {
  background: #c511c5;
}

/* ALERT */
.alert {
  font-size: 13px;
}

/* FOOTER */
.login-footer {
  margin-top: 15px;
  font-size: 12px;
  color: #999;
  text-align: center;
}

/* =========================
   RESPONSIVE (TABLET & DOWN)
========================= */
@media (max-width: 768px) {

  .login-page {
    justify-content: flex-start; /* ✅ REMOVE vertical centering on mobile */
    padding: 15px;
  }

  .login-box {
    flex-direction: column;
  }

  .login-brand {
    width: 100%;
    padding: 25px;
    align-items: flex-start; /* ✅ KEEP TOP ALIGN */
    text-align: left;
  }

  .login-form {
    width: 100%;
    padding: 25px;
  }
}

/* =========================
   SMALL PHONES
========================= */
@media (max-width: 480px) {

  .login-brand {
    padding: 20px;
  }

  .login-form {
    padding: 20px;
  }

  .form-header h4 {
    font-size: 18px;
  }
}

</style>